import React from 'react';
import { Link } from 'react-router-dom';
import { aboutContent } from '../mockData';
import { Target, Eye, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  return (
    <div style={{ backgroundColor: '#1a1c1b', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{
        padding: '120px 40px 80px',
        backgroundColor: '#302f2c',
        borderBottom: '1px solid rgba(63, 72, 22, 0.5)'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 0.95,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            About Worklink Global
          </h1>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            maxWidth: '50rem',
            margin: '0 auto'
          }}>
            Leading Australian workforce aggregation platform
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '48px',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              fontFamily: 'Arial, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              lineHeight: 1.1,
              color: '#d9fb06',
              textTransform: 'uppercase',
              marginBottom: '24px'
            }}>
              Who We Are
            </h2>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: '#dfddd6',
              marginBottom: '24px'
            }}>
              {aboutContent.whoWeAre}
            </p>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1rem',
              lineHeight: 1.6,
              color: '#888680',
              marginBottom: '20px'
            }}>
              We bridge the gap between industry demand and skilled talent — ensuring every project receives the right people, at the right time, under one unified framework.
            </p>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1.05rem',
              lineHeight: 1.5,
              color: '#888680',
              fontWeight: 500,
              fontStyle: 'italic'
            }}>
              Workforce and supply coordination, simplified.
            </p>
          </div>
          <div style={{
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(63, 72, 22, 0.5)'
          }}>
            <img
              src={aboutContent.image}
              alt="Team collaboration"
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover'
              }}
            />
          </div>
        </div>
      </section>

      {/* Mission, Vision & Values */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#302f2c'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '40px'
        }}>
          {[
            {
              icon: Target,
              title: 'Our Mission',
              content: aboutContent.mission
            },
            {
              icon: Eye,
              title: 'Our Vision',
              content: aboutContent.vision
            },
            {
              icon: Award,
              title: 'Our Commitment',
              content: 'Delivering excellence through verified suppliers, strict compliance, and unwavering dedication to safety and quality standards.'
            }
          ].map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div
                key={index}
                style={{
                  backgroundColor: '#1a1c1b',
                  padding: '40px',
                  borderRadius: '8px',
                  border: '1px solid rgba(63, 72, 22, 0.5)',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <IconComponent size={40} style={{ color: '#d9fb06', marginBottom: '20px' }} />
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#d9fb06',
                  marginBottom: '16px',
                  textTransform: 'uppercase'
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  color: '#dfddd6'
                }}>
                  {item.content}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Our Difference */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            Our Difference
          </h2>
          <div style={{
            backgroundColor: '#302f2c',
            padding: '48px',
            borderRadius: '8px',
            border: '1px solid rgba(63, 72, 22, 0.5)',
            marginBottom: '32px'
          }}>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1.25rem',
              lineHeight: 1.7,
              color: '#dfddd6',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              Unlike traditional labour hire companies, Worklink Global operates as a <strong style={{ color: '#d9fb06' }}>premium aggregator</strong>:
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px'
            }}>
              {[
                'Multiple suppliers, one point of contact',
                'Stronger compliance assurance',
                'Faster sourcing capability',
                'Quality-driven labour allocation',
                'Integrated materials & supply coordination',
                'Performance monitoring & supplier oversight'
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '20px',
                  backgroundColor: '#1a1c1b',
                  borderRadius: '8px',
                  border: '1px solid rgba(63, 72, 22, 0.5)'
                }}>
                  <p style={{
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontSize: '1rem',
                    color: '#dfddd6',
                    margin: 0
                  }}>
                    • {item}
                  </p>
                </div>
              ))}
            </div>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: '#888680',
              marginTop: '32px',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              This model reduces risk, improves efficiency, and drives higher project standards for our clients.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#3f4816',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '24px',
            textTransform: 'uppercase'
          }}>
            Partner With Excellence
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            marginBottom: '40px'
          }}>
            Join Australia's trusted workforce aggregation platform
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link to="/contact?form=workforce" style={{ textDecoration: 'none' }}>
              <Button
                style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Request Workforce
              </Button>
            </Link>
            <Link to="/services" style={{ textDecoration: 'none' }}>
              <Button
                style={{
                  background: 'transparent',
                  color: '#d9fb06',
                  border: '2px solid #d9fb06',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d9fb06';
                  e.currentTarget.style.color = '#1a1c1b';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#d9fb06';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Explore Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
